import React, { useState, useEffect } from 'react';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Input from '../../common/Input';
import Textarea from '../../common/Textarea';
import branchService from '../../../services/branchAddressService';

const BranchAddressManager = () => {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [defaultBranch, setDefaultBranch] = useState(null);
    const [settingDefault, setSettingDefault] = useState(false);
    const [deleteBranch, setDeleteBranch] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [messageModal, setMessageModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);

        try {
            const result = await branchService.getAll();

            if (result.success) {
                setBranches(result.data || []);
            } else {
                showMessage('Branch Address', result.error || 'Failed to fetch branch addresses');
            }
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (title, message) => {
        setMessageModal({
            isOpen: true,
            title,
            message
        });
    };

    const handleOpenModal = (branch = null) => {
        setEditingBranch(branch ? { ...branch } : { name: '', address: '' });
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!editingBranch.name.trim()) {
            showMessage('Branch Address', 'Branch name is required');
            return;
        }

        if (!editingBranch.address.trim()) {
            showMessage('Branch Address', 'Branch address is required');
            return;
        }

        setSubmitting(true);

        try {
            const result = editingBranch.id
                ? await branchService.update(editingBranch.id, editingBranch.name, editingBranch.address)
                : await branchService.create(editingBranch.name, editingBranch.address);

            if (result.success) {
                setIsModalOpen(false);
                fetchData();
            } else {
                showMessage('Branch Address', result.error || 'Failed to save branch address');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleSetDefaultClick = (branch) => {
        setDefaultBranch(branch);
    };

    const handleConfirmSetDefault = async () => {
        if (!defaultBranch) return;
        setSettingDefault(true);

        try {
            const result = await branchService.setDefault(defaultBranch.id);
            if (result.success) {
                setDefaultBranch(null);
                fetchData();
            } else {
                setDefaultBranch(null);
                showMessage('Set Default Branch', result.error || 'Failed to set default branch');
            }
        } finally {
            setSettingDefault(false);
        }
    };

    const handleDeleteClick = (branch) => {
        setDeleteBranch(branch);
    };

    const handleConfirmDelete = async () => {
        if (!deleteBranch) return;
        setDeleting(true);

        try {
            const result = await branchService.remove(deleteBranch.id);

            if (result.success) {
                setDeleteBranch(null);
                fetchData();
            } else {
                setDeleteBranch(null);
                showMessage('Delete Branch Address', result.error || 'Failed to delete branch address');
            }
        } finally {
            setDeleting(false);
        }
    };

    if (loading) return <div>Loading...</div>;

    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px'
    };

    const modalStyle = {
        background: 'var(--surface)',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
    };

    const smallModalStyle = {
        background: 'var(--surface)',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
        border: '1px solid var(--border)'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    Branch Addresses
                </h2>
                <Button onClick={() => handleOpenModal()}>Add New Branch</Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {branches.length === 0 && (
                    <p style={{ color: 'var(--text-secondary)' }}>
                        No branch addresses added yet.
                    </p>
                )}

                {branches.map(branch => (
                    <Card key={branch.id} title={branch.name} padding="20px">
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: '150px'
                            }}
                        >
                            <p
                                style={{
                                    color: 'var(--text-secondary)',
                                    fontSize: '13px',
                                    marginBottom: '16px',
                                    lineHeight: '1.4',
                                    flex: 1
                                }}
                            >
                                {branch.address}
                            </p>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: '10px',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    marginTop: 'auto'
                                }}
                            >
                                <Button variant="secondary" size="small" onClick={() => handleOpenModal(branch)}>
                                    Edit
                                </Button>

                                {branch.is_default && (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'var(--primary)',
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: '800',
                                            letterSpacing: '0.5px',
                                            padding: '7px 14px',
                                            borderRadius: '8px',
                                            textTransform: 'uppercase',
                                            minHeight: '34px'
                                        }}
                                    >
                                        DEFAULT
                                    </span>
                                )}

                                {!branch.is_default && (
                                    <Button variant="secondary" size="small" onClick={() => handleSetDefaultClick(branch)}>
                                        Set as Default
                                    </Button>
                                )}

                                {!branch.is_default && (
                                    <Button variant="danger" size="small" onClick={() => handleDeleteClick(branch)}>
                                        Delete
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {isModalOpen && (
                <div style={modalOverlayStyle}>
                    <div style={modalStyle}>
                        <div style={{
                            padding: '24px',
                            borderBottom: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}>
                            <h3 style={{ margin: 0 }}>
                                {editingBranch.id ? 'Edit Branch' : 'New Branch'}
                            </h3>

                            <button
                                onClick={() => setIsModalOpen(false)}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                            <Input
                                label="Branch Name"
                                value={editingBranch.name}
                                onChange={(e) => setEditingBranch({ ...editingBranch, name: e.target.value })}
                                placeholder="e.g. Pune"
                            />

                            <div style={{ marginTop: '16px' }}>
                                <Textarea
                                    label="Branch Address"
                                    value={editingBranch.address}
                                    onChange={(e) => setEditingBranch({ ...editingBranch, address: e.target.value })}
                                    placeholder="Enter full branch address with contact number"
                                    rows={4}
                                />
                            </div>
                        </div>

                        <div style={{
                            padding: '24px',
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                        }}>
                            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleSave} disabled={submitting}>
                                {submitting ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {defaultBranch && (
                    <div style={modalOverlayStyle}>
                        <div style={smallModalStyle}>
                            <div style={{
                                padding: '24px',
                                borderBottom: '1px solid var(--border)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                                    Set Default Branch
                                </h3>

                                <button
                                    onClick={() => setDefaultBranch(null)}
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        cursor: 'pointer',
                                        color: 'var(--text-secondary)'
                                    }}
                                >
                                    ✕
                                </button>
                            </div>

                            <div style={{ padding: '24px', color: 'var(--text-primary)', fontSize: '14px' }}>
                                Do you want to set <strong>"{defaultBranch.name}"</strong> as the default branch address?
                            </div>

                            <div style={{
                                padding: '20px 24px',
                                borderTop: '1px solid var(--border)',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px'
                            }}>
                                <Button
                                    variant="secondary"
                                    onClick={() => setDefaultBranch(null)}
                                    disabled={settingDefault}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    variant="primary"
                                    onClick={handleConfirmSetDefault}
                                    disabled={settingDefault}
                                >
                                    {settingDefault ? 'Setting...' : 'Set Default'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            {deleteBranch && (
                <div style={modalOverlayStyle}>
                    <div style={smallModalStyle}>
                        <div style={{
                            padding: '24px',
                            borderBottom: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                                Delete Branch Address
                            </h3>

                            <button
                                onClick={() => setDeleteBranch(null)}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ padding: '24px', color: 'var(--text-primary)', fontSize: '14px' }}>
                            Do you want to delete <strong>"{deleteBranch.name}"</strong> branch address?
                        </div>

                        <div style={{
                            padding: '20px 24px',
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px'
                        }}>
                            <Button
                                variant="secondary"
                                onClick={() => setDeleteBranch(null)}
                                disabled={deleting}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="danger"
                                onClick={handleConfirmDelete}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {messageModal.isOpen && (
                <div style={modalOverlayStyle}>
                    <div style={smallModalStyle}>
                        <div style={{
                            padding: '24px',
                            borderBottom: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                                {messageModal.title}
                            </h3>

                            <button
                                onClick={() => setMessageModal({ isOpen: false, title: '', message: '' })}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                            >
                                X
                            </button>
                        </div>

                        <div style={{ padding: '24px', color: 'var(--text-primary)', fontSize: '14px' }}>
                            {messageModal.message}
                        </div>

                        <div style={{
                            padding: '20px 24px',
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}>
                            <Button
                                variant="primary"
                                onClick={() => setMessageModal({ isOpen: false, title: '', message: '' })}
                            >
                                OK
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BranchAddressManager;